import React, { useState, useEffect, useMemo, useRef } from 'react';
import { fetchColumnsData } from '../dispatchers/ColumnSelectorDispatcher';
import { useAppDispatch, useAppSelector } from '../hooks/storeHooks';
import SuccessfullToast from '../features/common/toast/SuccessfullToast';
import ErrorToast from '../features/common/toast/ErrorToast';
import Dashboard from '../components/time-series-chart/Dashboard';
import 'react-grid-layout/css/styles.css';
import { Layout } from 'react-grid-layout';
import RGL, { WidthProvider } from 'react-grid-layout';
import { ToastServiceProvider } from '../components/toast/ToastService';
import { useParams } from 'react-router-dom';
import { filterWidgetItemnsForTestScreen } from '../well-chart/WidgetLayoutService';
import WellTestTableWidget from '../well-test-table/WellTestTableWidget';
import { fetchWellTestId } from '../dispatchers/WellTestDispatcher';
import Loader from '../features/common/Loader/Loader';
import { fetchLicenseAccessDetails } from '../dispatchers/WidgetAccessDispatcher';
import { AccessiableWidget } from '../models/LicenseWidget';
import NoAccessWidget from '../components/no-access-widget/NoAccessWidget';
import useConfig from '../hooks/useConfig';
import { fetchDefaultWellTestDataAsync } from '../dispatchers/DefaultWellTestDispatcher';
import { ProtectComponent } from '../rbac';
import { R, WIDGETS } from '../rbac';
import './WellTest.scss';

const GridLayout = WidthProvider(RGL);

export default function Main() {
  const chartType = useAppSelector((state) => state.AssetAnalysis.chartType);
  const [isAddTestModalOpen, setIsAddTestModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isAddNoteWell, setIsAddNoteWell] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useAppDispatch();
  const { enablePumpChecker, enableLicenseCheck } = useConfig();
  const licenseAccessDetails = useAppSelector((state: any) => state.widgetAccessSlice.data);
  const wellTestPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const wellTestData = wellTestPageData?.find((obj: any) => obj.key === 'wellTestChart');
  const defaultLayout = wellTestData?.widgetProperty?.widgetProperties?.defaultLayout || 'All';
  const [layout, setLayout] = useState(defaultLayout || 'All');
  const dashboardName = useAppSelector((state: any) => state.AssetAnalysis.chartType);

  const wellTestWidget = wellTestPageData?.find((obj: any) => obj.key === 'wellTestTable');
  const timeseriesChartData = wellTestPageData?.find(
    (obj: any) => obj.key === (layout === 'All' ? 'wellTestChart' : 'wellTestSplitChart'),
  );
  const columnObjects = wellTestWidget?.widgetProperty?.widgetProperties?.columns || [];
  const { assetId } = useParams<{ assetId: string }>();

  const chartViewType = useAppSelector(
    (state) =>
      state.testWellColumnsSlice.data?.widgets?.find((w: any) => w.key === 'wellTestChart')?.widgetProperty
        ?.widgetProperties?.chartType,
  );

  useEffect(() => {
    if (chartViewType && layout !== chartViewType) {
      setLayout(chartViewType);
    }
  }, [chartViewType]);

  useEffect(() => {
    if (assetId) {
      dispatch(fetchWellTestId(assetId));

      // Fetch Default well test data when enablePumpChecker is false
      if (!enablePumpChecker) {
        dispatch(fetchDefaultWellTestDataAsync(assetId));
      }
    }
  }, [assetId, enablePumpChecker]);

  // Refetch columns data when layout changes to sync latest date preferences
  // Works for any layout value change (All/Split or any other value)
  // IMPORTANT: This is specific to WellTest component (test tab) and will NOT affect WellChart (charts tab)
  useEffect(() => {
    // Additional safeguard: Only run for test dashboard (chartType contains 'tests')
    const isTestDashboard = chartType?.toLowerCase().includes('tests');
    if (!isTestDashboard) return;

    // Skip initial render - only refetch when layout actually changes
    if (previousLayoutRef.current !== null && previousLayoutRef.current !== layout && chartType) {
      dispatch(fetchColumnsData(chartType));
    }
    // Update the ref to track the current layout
    previousLayoutRef.current = layout;
  }, [layout, chartType, dispatch]);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [saveConfigResponse, setSaveConfigResponse] = useState(false);

  const testWellData = useAppSelector((state: any) => state.testWellDataSlice.data);
  const t1WellTestData = useAppSelector((state: any) => state.DefaultWellTest.data);
  const wellTestIdData = useAppSelector((state: any) => state.WellTestIdSlice?.data);
  const wellTestObject = wellTestPageData;

  const sortedColumnObjects = useMemo(() => {
    return [...columnObjects].sort((a, b) => a.order - b.order);
  }, [columnObjects]);

  const dataTableRef = useRef<HTMLDivElement>(null);
  const previousLayoutRef = useRef<string | null>(null);

  interface GridLayoutItem {
    i: string;
    x: number;
    y: number;
    w: number;
    h: number;
    minH?: number;
    minW?: number;
    moduleId?: number;
  }

  const safeLayout = (wellTestObject: any[], layout: string): Layout[] => {
    wellTestObject = filterWidgetItemnsForTestScreen(wellTestObject, layout);
    return wellTestObject
      ?.map((obj: any, index: number): GridLayoutItem | null => {
        const lg = obj?.layout?.lg;
        if (!lg || !obj?.key) {
          console.warn(`Invalid layout at index ${index}`, obj);
          return null;
        }

        const item: GridLayoutItem = {
          i: String(obj.key),
          x: lg.x,
          y: lg.y,
          w: lg.width,
          h: lg.height,
          minH: lg.minH ?? 1,
          minW: lg.minW ?? 6,
          moduleId: obj.widgetProperty?.moduleId,
        };

        return item;
      })
      .filter((item): item is GridLayoutItem => item !== null);
  };

  const layoutMemo = useMemo(() => safeLayout(wellTestObject, layout), [wellTestObject, layout]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchLicenseAccessDetails());
      await dispatch(fetchColumnsData(chartType));
      setIsLoading(false);
    };

    fetchData();
  }, [dispatch, chartType]);

  // Render a loading spinner or message while data is being fetched
  if (isLoading) {
    return <Loader />;
  }

  const handleOpenAddTaskModal = (status: boolean) => {
    setIsAddTaskModalOpen(status);
  };

  const handleOpenAddWellNoteModal = (status: boolean) => {
    setIsAddNoteWell(status);
  };

  const handleTaskSaved = () => {
    setIsAddTaskModalOpen(false);
  };

  const isWidgetAccessible = (moduleId: number): boolean => {
    if (!enableLicenseCheck) return true;
    return licenseAccessDetails?.features?.some((x: AccessiableWidget) => x.moduleId === moduleId);
  };

  return (
    <ToastServiceProvider>
      <div className='chart-component-container'>
        <SuccessfullToast
          isShowEnabled={saveConfigResponse}
          message={successMessage}
          callback={() => setSuccessMessage('')}
        />
        <ErrorToast isShowEnabled={!!errorMessage} message={errorMessage} callback={() => setErrorMessage('')} />
        <GridLayout
          layout={layoutMemo}
          cols={12}
          rowHeight={30}
          isDraggable={false}
          isResizable={true}
          className='well-tests-layout-react-grid'
          useCSSTransforms={false}
          isDroppable={false}
        >
          {layoutMemo?.map((item: any) => (
            <div key={item.i} id={`grid-item-${item.i}`} className='grid-item'>
              {item.i === (layout === 'All' ? 'wellTestChart' : 'wellTestSplitChart') && (
                <Dashboard
                  timeseriesChartData={timeseriesChartData}
                  openAddTaskModal={handleOpenAddTaskModal}
                  openAddNoteModal={handleOpenAddWellNoteModal}
                  layout={layout}
                  accessible={isWidgetAccessible(item.moduleId)}
                  widgetName={item.i}
                  className='well-tests-main-container'
                />
              )}

              {item.i === 'wellTestTable' &&
                (!isWidgetAccessible(item.moduleId) ? (
                  <NoAccessWidget widgetName='Well tests' />
                ) : (
                  <ProtectComponent
                    resource={R.widget(dashboardName, WIDGETS.WELL_TEST_TABLE)}
                    action='view'
                    widgetName='Well tests'
                    widgetMessageName='well tests'
                  >
                    <main className='well-tests-main-container table-wrap'>
                      <WellTestTableWidget
                        wellTestIdData={wellTestIdData}
                        isAddTestModalOpen={isAddTestModalOpen}
                        setIsAddTestModalOpen={setIsAddTestModalOpen}
                        isAddTaskModalOpen={isAddTaskModalOpen}
                        setIsAddTaskModalOpen={setIsAddTaskModalOpen}
                        isAddNoteWell={isAddNoteWell}
                        setIsAddNoteWell={setIsAddNoteWell}
                        sortedColumnObjects={sortedColumnObjects}
                        dataTableRef={dataTableRef}
                        columnObjects={columnObjects}
                        wellTestWidget={wellTestWidget}
                        assetId={assetId}
                        testWellData={enablePumpChecker ? testWellData : t1WellTestData?.values || []}
                        handleTaskSaved={handleTaskSaved}
                        setSaveConfigResponse={setSaveConfigResponse}
                        setSuccessMessage={setSuccessMessage}
                        setErrorMessage={setErrorMessage}
                        widgetName={item.i}
                      />
                    </main>
                  </ProtectComponent>
                ))}
            </div>
          ))}
        </GridLayout>
      </div>
    </ToastServiceProvider>
  );
}
